/**
 * context/app/useLiveLists.js - keeping the lists current: other people's
 * changes over the live stream, the quiet re-read after this tab's own
 * writes, and a saved / deleted project placed straight into the lists.
 * Moved verbatim out of AppProvider (AppContext.jsx), which calls it at the
 * same point (same hook order) with the state it reads.
 */
import { useCallback, useRef, useEffect } from 'react';
import { getRole } from '../../data/admins';
import { startLive, subscribe, stopLive } from '../../live/liveEvents';
import { createBatcher } from '../../live/useLive';
import { notifyCommentsChanged } from '../../hooks/useCommentsIndex';
import { notifyDocsChanged } from '../../hooks/useDocIndex';
import { listedProject, withSavedRow } from './rows';
import { getList, getMyAccess, sameAccess, listsFor, LIVE_LISTS, liveTargets } from './apiClient';

export function useLiveLists({
  user,
  gens,
  usersRef,
  myAccessRef,
  setUsers,
  setMyAccess,
  fetchAll,
  accessNow,
  setAllAccounts,
  setAllProjects,
  setAllOpportunities,
  setAllEngineers,
}) {
  /* ── Live: other people's changes ──
     Someone adds an opportunity, edits an account, moves an engineer: every
     other signed-in tab hears it (src/live) and re-reads ONLY the lists that
     change can have touched, quietly - no `loading` flip, so nothing blinks,
     the new row is just there. A failed re-read keeps what is on screen (the
     next change or a resync catches up) and counts for nothing (readers).
     This tab's own saves are not echoed back: they put the saved row in
     and re-read through refreshQuietly (below). */
  const reloadLive = useCallback(
    async (targets) => {
      if (!user) return;
      const want = new Set(targets);
      if (want.has('users')) {
        // someone's role or access changed - perhaps mine: re-read my own
        // access with the roster, so my tabs and buttons follow it
        const t = gens.users.start();
        const [roster, mine] = await Promise.all([getList('/api/users'), getMyAccess()]);
        const beforeRole = getRole(user.email, usersRef.current);
        const beforeAccess = myAccessRef.current;
        if (roster && gens.users.show(t)) {
          usersRef.current = roster;
          setUsers(roster);
        }
        if (mine && !sameAccess(mine, beforeAccess)) {
          myAccessRef.current = mine;
          setMyAccess(mine);
        }
        // my own role or access changed: what this tab may read changed with it
        const roleMoved = roster && getRole(user.email, roster) !== beforeRole;
        if (roleMoved || (mine && beforeAccess && !sameAccess(mine, beforeAccess))) {
          await fetchAll();
          return;
        }
      }
      const can = listsFor(accessNow());
      const jobs = [
        ['accounts', (rows) => setAllAccounts(rows)],
        [
          'projects',
          (rows) => {
            setAllProjects(rows);
            setAllOpportunities(rows);
          },
        ],
        ['engineers', (rows) => setAllEngineers(rows)],
      ].filter(([key]) => want.has(key) && can[key]);
      await Promise.all(
        jobs.map(async ([key, apply]) => {
          const t = gens[key].start();
          const rows = await getList(`/api/${key}`);
          if (rows && gens[key].show(t)) apply(rows);
        })
      );
    },
    [
      user,
      fetchAll,
      gens,
      accessNow,
      myAccessRef,
      setAllAccounts,
      setAllEngineers,
      setAllOpportunities,
      setAllProjects,
      setMyAccess,
      setUsers,
      usersRef,
    ]
  );

  const reloadLiveRef = useRef(reloadLive);
  useEffect(() => {
    reloadLiveRef.current = reloadLive;
  }, [reloadLive]);

  /* ── After this tab's own writes ──
     refreshQuietly(lists) re-reads just those lists, the way a live change
     does: no `loading` flip, nothing blinks. Calls that overlap are
     coalesced - while one runs, every later call joins ONE follow-up run with
     the union of their lists - so a burst of saves is two reads, not ten.
     Never throws; a failed read keeps what is on screen. */
  const quiet = useRef({ busy: null, queued: null, next: null });
  const refreshQuietly = useCallback((lists) => {
    const q = quiet.current;
    const run = (l) => {
      const p = Promise.resolve()
        .then(() => reloadLiveRef.current(l))
        .catch((e) => console.warn('refresh failed:', e && e.message))
        .finally(() => {
          if (q.busy === p) q.busy = null;
        });
      q.busy = p;
      return p;
    };
    if (!q.busy && !q.next) return run(lists);
    q.queued = new Set([...(q.queued || []), ...lists]);
    if (!q.next) {
      q.next = q.busy.then(() => {
        const l = [...q.queued];
        q.queued = null;
        q.next = null;
        return run(l);
      });
    }
    return q.next;
  }, []);

  /* A project row the server just answered with goes into the lists at
     once. Reads already out (a live re-read started before the save) are
     outranked, so a late answer cannot put the old row back. */
  const applySavedProject = useCallback(
    (raw) => {
      if (!raw || typeof raw !== 'object' || raw.id === null || raw.id === undefined) return;
      const row = listedProject(raw);
      gens.projects.show(gens.projects.start());
      setAllProjects((rows) => withSavedRow(rows, row));
      setAllOpportunities((rows) => withSavedRow(rows, row));
    },
    [gens, setAllOpportunities, setAllProjects]
  );
  const dropProject = useCallback(
    (id) => {
      gens.projects.show(gens.projects.start());
      const gone = (rows) => rows.filter((r) => String(r.id) !== String(id));
      setAllProjects(gone);
      setAllOpportunities(gone);
    },
    [gens, setAllOpportunities, setAllProjects]
  );
  /** The row is in; the lists catch up behind it (not awaited by a save). */
  const settleProject = useCallback(
    (raw, { accounts = true } = {}) => {
      applySavedProject(raw);
      refreshQuietly(accounts ? ['projects', 'accounts'] : ['projects']);
    },
    [applySavedProject, refreshQuietly]
  );

  // One connection per signed-in tab, opened here and nowhere else. Keyed on
  // the email, not the user object, which is rebuilt on every auth render.
  const liveEmail = user?.email || '';
  useEffect(() => {
    if (!liveEmail) return undefined;
    startLive();
    const batch = createBatcher((keys) => {
      if (keys.includes('comments')) notifyCommentsChanged();
      if (keys.includes('docs')) notifyDocsChanged();
      const lists = keys.filter((k) => LIVE_LISTS.includes(k));
      return lists.length ? reloadLiveRef.current(lists) : undefined;
    });
    const off = subscribe((evt) => {
      const keys = liveTargets(evt);
      if (keys.length) batch.add(keys);
    });
    return () => {
      off();
      batch.stop();
      stopLive();
    };
  }, [liveEmail]);
  return { refreshQuietly, dropProject, settleProject, applySavedProject };
}
